import { Page } from "modules/Page";
import {
    CombinedPressReleaseItem,
    CombinedPressReleaseApi,
    combinedPressReleaseApi,
    CombinedPressReleaseQueryParams,
} from "api/CombinedPressReleaseApi";
import { Select } from "components/Select/Select";
import { debounce } from "modules/debounce";

import combinedPressReleaseTemplate from "./CombinedPressReleasePage.hbs";
import mobileFilterTemplate from "../../components/Filters/FilterMobile.hbs";

enum SelectFilterType {
    Year = "year",
    Type = "type",
}

enum FilterType {
    Page = "page",
    Language = "lang",
}

type FilterState = {
    selectedValues: {
        [SelectFilterType.Year]: string | null;
        [SelectFilterType.Type]: string | null;
    };
    [FilterType.Page]: number;
    [FilterType.Language]?: string;
};

type State = {
    combinedPressReleases: CombinedPressReleaseItem[];
    filters: FilterState;
    totalPages: number;
    totalPressReleases: number;
};

type YearGroup = { year: string; items: CombinedPressReleaseItem[] };

export class CombinedPressReleasePage extends Page<typeof CombinedPressReleasePage> {
    static readonly className = "js-combined-press-release-page";

    private pageID: string;
    private searchParams: URLSearchParams;

    private listElement: HTMLElement;            
    private loaderElement: HTMLElement;             
    private loadMoreContainer: HTMLElement | null;
    private loadMoreButton: HTMLButtonElement | null;
    private filtersContainer: HTMLElement | null;

    private selects: { [key in SelectFilterType]: Select | null } = {
        [SelectFilterType.Year]: null,
        [SelectFilterType.Type]: null,
    };

    private state: State = {
        combinedPressReleases: [],
        filters: {
            selectedValues: {
                [SelectFilterType.Year]: null,
                [SelectFilterType.Type]: null,
            },
            [FilterType.Page]: 1,
        },
        totalPages: 0,
        totalPressReleases: 0,
    };

    constructor(
        rootElement: HTMLElement,
        index: number,
        private readonly api: CombinedPressReleaseApi = combinedPressReleaseApi
    ) {
        super(rootElement, index);

        this.searchParams = new URLSearchParams(window.location.search);
        this.pageID = rootElement.dataset.pageid || "0";

        const lang = document.documentElement.getAttribute("lang");
        if (lang) this.state.filters[FilterType.Language] = lang;

        // Elements inside .list__wrapper
        this.listElement = rootElement.querySelector<HTMLElement>("#news__list")!;
        this.loaderElement = rootElement.querySelector<HTMLElement>(".list__loader")!;
        this.loadMoreContainer = rootElement.querySelector<HTMLElement>(".load-more"); // NEW
        this.loadMoreButton = rootElement.querySelector<HTMLButtonElement>("#load-more-button");
        this.filtersContainer = rootElement.querySelector(".filters");

        if (!this.loadMoreContainer) {
            console.warn("[CombinedPressRelease] .load-more container not found.");
        }
        if (!this.loadMoreButton) {
            console.warn("[CombinedPressRelease] #load-more-button not found.");
        }

        this.initSelects();
        this.initFilterGroup();
        this.loadMoreButton?.addEventListener("click", () => { void this.loadNextPage(); });

        this.refreshCombinedPressReleases();
    }

    /* -------------------------------- Filters -------------------------------- */

    private initSelects() {
        Object.values(SelectFilterType).forEach((filterType) => {
            const select = this.getComponent<Select>(`[name='${filterType}']`);
            if (!select) return;

            this.selects[filterType] = select;
            select.on("change", (values: string[]) => {
                const value = values[0] || null;
                this.state.filters.selectedValues[filterType] = value;
                this.state.filters[FilterType.Page] = 1;

                // Keep URL in sync (shareable filters)
                const params = new URLSearchParams(window.location.search);
                if (value) params.set(filterType, value);
                else params.delete(filterType);
                history.replaceState({}, "", `${location.pathname}?${params.toString()}`);

                this.refreshCombinedPressReleases();
            });

            // Initialize from URL if present
            const paramValue = this.searchParams.get(filterType);
            if (paramValue) {
                this.state.filters.selectedValues[filterType] = paramValue;
                select.setSelectedValues([paramValue]);
            }
        });
    }

    private initFilterGroup() {
        const main = document.querySelector("main");
        if (!main || !this.filtersContainer) return;

        const mobileButton = document.createElement("div");
        mobileButton.className = "filter-mobile";
        mobileButton.innerHTML = mobileFilterTemplate({
            label: this.filtersContainer.dataset.labelFilterMobile || "Filter",
        });

        main.appendChild(mobileButton);

        mobileButton.addEventListener("click", () => {
            this.filtersContainer?.classList.add("active");
            (main).classList.add("disabled");
        });

        document.getElementById("filters-collapse-mobile")?.addEventListener("click", () => {
            this.filtersContainer?.classList.remove("active");
            (main).classList.remove("disabled");
        });

        document.getElementById("filter-clear")?.addEventListener("click", () => {
            this.clearAllFilters();
        });
    }

    private clearAllFilters() {
        Object.values(SelectFilterType).forEach((filterType) => {
            this.selects[filterType]?.clearOptions();
            this.selects[filterType]?.resetLabels();
            this.state.filters.selectedValues[filterType] = null;
        });

        this.state.filters[FilterType.Page] = 1;

        const params = new URLSearchParams(window.location.search);
        params.delete(SelectFilterType.Year);
        params.delete(SelectFilterType.Type);
        history.replaceState({}, "", `${location.pathname}?${params.toString()}`);

        this.refreshCombinedPressReleases();
    }

    /* ------------------------------- Data loads ------------------------------- */

    private refreshCombinedPressReleases = debounce(async () => {
        this.showLoader(true);

        const params = this.getRequestParams();

        try {
            const data = await this.api.getPageData(params);

            // Update state (API already returns numbers; fallback to 0 if absent)
            this.state.combinedPressReleases = this.sortByDateThenTitle(data.combinedPressReleases ?? []);
            this.state.totalPages = typeof data.totalPages === "number" ? data.totalPages : 0;
            this.state.totalPressReleases = typeof data.totalPressReleases === "number" ? data.totalPressReleases : 0;

            // Render grouped by year (desc)
            this.renderList();

            // On refresh, page is 1; decide if more pages exist
            const hasMore = this.state.filters[FilterType.Page] < this.state.totalPages;
            this.setLoadMoreVisibility(hasMore);

            this.showLoader(false);
        } catch (e) {
            console.error("[CombinedPressRelease] refresh error:", e);
            this.setLoadMoreVisibility(false);
            this.showLoader(false);
        }
    }, 300);

    private loadNextPage = async () => {
        if (!this.loadMoreButton) return;

        this.loadMoreButton.disabled = true;
        this.state.filters[FilterType.Page] += 1;

        const params = this.getRequestParams();

        try {
            const data = await this.api.getPageData(params);

            // Merge + sort globally (newest → oldest)
            const merged = [...this.state.combinedPressReleases, ...(data.combinedPressReleases ?? [])];
            this.state.combinedPressReleases = this.sortByDateThenTitle(merged);

            // Update totals if provided
            if (typeof data.totalPages === "number") this.state.totalPages = data.totalPages;
            if (typeof data.totalPressReleases === "number") this.state.totalPressReleases = data.totalPressReleases;

            // Re-render grouped list
            this.renderList();

            // Update container visibility and button state
            const hasMore = this.state.filters[FilterType.Page] < this.state.totalPages;
            this.setLoadMoreVisibility(hasMore);
        } catch (e) {
            console.error("[CombinedPressRelease] loadNextPage error:", e);
            // Re-enable so the user can retry
            if (this.loadMoreButton) this.loadMoreButton.disabled = false;
        }
    };

    /* --------------------------------- Utils --------------------------------- */

    
    private showLoader(isLoading: boolean) {
        this.loaderElement.style.display = isLoading ? "flex" : "none";
        this.listElement.style.display = isLoading ? "none" : "block";
        this.listElement.setAttribute("aria-busy", String(isLoading));

        // Optional: hide the container while loading to avoid flicker
        if (isLoading && this.loadMoreContainer) {
            this.loadMoreContainer.style.display = "none";
        }
    }

    
    private setLoadMoreVisibility(hasMore: boolean) {
        if (this.loadMoreContainer) {
            // Your CSS sets `.load-more { display:none }` by default.
            // Show it as flex when we have more pages.
            this.loadMoreContainer.style.display = hasMore ? "flex" : "none";
        }
        if (this.loadMoreButton) {
            this.loadMoreButton.disabled = !hasMore;
        }
    }

    private getRequestParams(): CombinedPressReleaseQueryParams {
        const params: CombinedPressReleaseQueryParams = {
            page: this.state.filters[FilterType.Page],
            pageID: this.pageID,
        };

        const { year, type } = this.state.filters.selectedValues;
        if (year) params.year = year;
        if (type) params.type = type;

        const lang = this.state.filters[FilterType.Language];
        if (lang) params.lang = lang;

        return params;
    }

    
    private getYearGroups(items: CombinedPressReleaseItem[]): YearGroup[] {
        const grouped: Record<string, CombinedPressReleaseItem[]> = {};

        for (const item of items) {
            const yearKey = String(
                item.year ??
                (item.publishDateFormatted ? new Date(item.publishDateFormatted).getFullYear() : "")
            );
            (grouped[yearKey] ??= []).push(item);
        }

        // Sort items in each year: newest → oldest; tie-breaker on name
        for (const year of Object.keys(grouped)) {
            grouped[year] = this.sortByDateThenTitle(grouped[year]);
        }

        // Convert to array and sort years DESC numerically
        const yearGroups: YearGroup[] = Object.keys(grouped)
            .sort((a, b) => Number(b) - Number(a))
            .map((year) => ({ year, items: grouped[year] }));

        return yearGroups;
    }

    
    private sortByDateThenTitle(arr: CombinedPressReleaseItem[]): CombinedPressReleaseItem[] {
        const copy = [...arr];
        copy.sort((a, b) => {
            const diff = Date.parse(b.publishDateFormatted) - Date.parse(a.publishDateFormatted);
            if (diff !== 0) return diff;
             
            const an = a.name ?? "";
             
            const bn = b.name ?? "";
            return String(an).localeCompare(String(bn));
        });
        return copy;
    }
    private renderList() {
        const yearGroups = this.getYearGroups(this.state.combinedPressReleases);
        this.listElement.innerHTML = combinedPressReleaseTemplate({ yearGroups });
    }
}